import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Image, Popover, Skeleton, Spin } from 'antd';
import { useMachineDashboard } from '../../hooks/MachineDashboardContext';
import { usePopoverViewportPlacement } from '../../hooks/usePopoverViewportPlacement';
import { MAX_POPOVER_HEIGHT_PX, StyledMachineKpiPopover } from './MachineKpiPopover.styles';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { ReactComponent as CancelIcon } from 'lib/assets/images/icons/Cancel_Icon.svg';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { MachineDashboardWidgetSection } from 'app/modules/cleaning/widgets/machine-dashboard/types/MachineDashboard.types';
import { MachinesReport } from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import {
  getDashboardMachineDuration,
  getDashboardMachineLastConnection,
  getDashboardMachineManager,
  getDashboardMachineName,
  getDashboardMachineSite,
} from 'app/modules/cleaning/widgets/machine-dashboard/utils/machineDashboardWidgetListUtils';
import { buildMachineDetailsTabPath } from 'app/modules/machine-inventory/machine-details-panel/hooks/useMachineDetailsTabFromUrl';
import { RoutePaths } from 'config/route-paths';

interface MachineKpiPopoverProps {
  title: string;
  machines: MachinesReport[];
  thumbnailUrlsByMachineId: Record<string, string>;
  hintText: string;
  widgetSection: MachineDashboardWidgetSection;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  children: React.ReactNode;
}

export const MachineKpiPopover = ({
  title,
  machines,
  thumbnailUrlsByMachineId,
  hintText,
  widgetSection,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  children,
}: MachineKpiPopoverProps): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleNavigateToDeviceTable } = useMachineDashboard();

  const [open, setOpen] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { triggerRef, placement, maxHeight, recalculatePlacement } = usePopoverViewportPlacement(
    open,
    MAX_POPOVER_HEIGHT_PX
  );

  const checkAtBottom = useCallback((el: HTMLDivElement): boolean => {
    if (el.scrollHeight <= el.clientHeight) {
      return true;
    }

    return el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
  }, []);

  const shouldLoadMore = useCallback((el: HTMLDivElement): boolean => {
    if (el.scrollHeight <= el.clientHeight) {
      return false;
    }

    return el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
  }, []);

  const handleScroll = useCallback(() => {
    const scrollDiv = scrollRef.current;
    if (!scrollDiv) return;

    setIsAtBottom(checkAtBottom(scrollDiv));

    if (shouldLoadMore(scrollDiv) && hasMore && !isLoadingMore && !isLoading) {
      onLoadMore();
    }
  }, [checkAtBottom, shouldLoadMore, hasMore, isLoading, isLoadingMore, onLoadMore]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean): void => {
      if (nextOpen) {
        setIsAtBottom(false);
        recalculatePlacement();
      }
      setOpen(nextOpen);
    },
    [recalculatePlacement]
  );

  useEffect(() => {
    if (!open || isLoading) return;
    const scrollDiv = scrollRef.current;
    if (!scrollDiv) return;

    setIsAtBottom(checkAtBottom(scrollDiv));

    const hasNoOverflow = scrollDiv.scrollHeight <= scrollDiv.clientHeight;
    if (hasNoOverflow && hasMore && !isLoadingMore) {
      onLoadMore();
    }
  }, [open, isLoading, machines.length, checkAtBottom, hasMore, isLoadingMore, onLoadMore]);

  const renderBody = (): React.JSX.Element => {
    if (isLoading) {
      return (
        <>
          <div className="machine-kpi-popover__skeleton">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="machine-kpi-popover__skeleton-item">
                <Skeleton.Avatar active shape="square" size={40} className="machine-kpi-popover__skeleton-thumbnail" />
                <div className="machine-kpi-popover__skeleton-content">
                  <Skeleton.Input active size="small" block className="machine-kpi-popover__skeleton-name" />
                  <Skeleton.Input active size="small" block />
                  <Skeleton.Input active size="small" block />
                  <Skeleton.Input active size="small" block />
                  <Skeleton.Input active size="small" block />
                </div>
                <Skeleton.Avatar active shape="circle" size={32} className="machine-kpi-popover__skeleton-arrow" />
              </div>
            ))}
          </div>
          <div className="machine-kpi-popover__hint-skeleton">
            <Skeleton active title={false} paragraph={{ rows: 2 }} />
          </div>
        </>
      );
    }

    return (
      <div
        className={`machine-kpi-popover__list-wrapper${
          isAtBottom ? ' machine-kpi-popover__list-wrapper--at-bottom' : ''
        }`}
      >
        <div className="machine-kpi-popover__list-scroll" ref={scrollRef} onScroll={handleScroll}>
          {machines.map(machine => {
            const thumbnailUrl = thumbnailUrlsByMachineId[machine.machineId];
            const machineName = getDashboardMachineName(machine);

            return (
              <div key={machine.machineId} className="machine-kpi-popover__item">
                <div className="machine-kpi-popover__item-thumbnail">
                  {thumbnailUrl ? (
                    <Image src={thumbnailUrl} alt={machineName} width={40} height={40} />
                  ) : (
                    <SvgIcon name="machine" />
                  )}
                </div>

                <div className="machine-kpi-popover__item-content">
                  <p className="machine-kpi-popover__item-name">{machineName}</p>
                  <div className="machine-kpi-popover__item-row">
                    <span className="machine-kpi-popover__item-label">{t('machineDashboard.popup.duration')}</span>
                    <span className="machine-kpi-popover__item-value">{getDashboardMachineDuration(machine)}</span>
                  </div>
                  <div className="machine-kpi-popover__item-row">
                    <span className="machine-kpi-popover__item-label">
                      {t('machineDashboard.popup.lastConnection')}
                    </span>
                    <span className="machine-kpi-popover__item-value">
                      {getDashboardMachineLastConnection(machine)}
                    </span>
                  </div>
                  <div className="machine-kpi-popover__item-row">
                    <span className="machine-kpi-popover__item-label">{t('machineDashboard.popup.site')}</span>
                    <span className="machine-kpi-popover__item-value">{getDashboardMachineSite(machine)}</span>
                  </div>
                  <div className="machine-kpi-popover__item-row">
                    <span className="machine-kpi-popover__item-label">{t('machineDashboard.popup.manager')}</span>
                    <span className="machine-kpi-popover__item-value">{getDashboardMachineManager(machine)}</span>
                  </div>
                </div>

                <KccButton
                  type="tertiary"
                  className="machine-kpi-popover__item-arrow"
                  aria-label={machineName}
                  icon={<SvgIcon name="rightChevron" />}
                  onClick={(): void => {
                    const path = buildMachineDetailsTabPath(
                      machine.machineId,
                      'OVERVIEW',
                      RoutePaths.MACHINE_DASHBOARD
                    );
                    navigate(path);
                    setOpen(false);
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const popoverContent = (
    <StyledMachineKpiPopover className="machine-kpi-popover" style={{ maxHeight }}>
      <div className="machine-kpi-popover__header">
        <p className="machine-kpi-popover__title">{t(title)}</p>
        <KccButton
          type="link"
          className="machine-kpi-popover__close"
          onClick={(): void => setOpen(false)}
          aria-label={t('common.close')}
          icon={<CancelIcon width={24} height={24} />}
        />
      </div>

      <div className="machine-kpi-popover__body">
        {renderBody()}

        {isLoadingMore && (
          <div className="machine-kpi-popover__loader">
            <Spin size="small" />
          </div>
        )}

        {!isLoading && (
          <div className="machine-kpi-popover__hint">
            <p className="machine-kpi-popover__hint-text">{t(hintText)}</p>
            <SvgIcon name="lightOn" />
          </div>
        )}
      </div>

      <div className="machine-kpi-popover__footer">
        <KccButton
          size="small"
          type="secondary"
          onClick={(): void => {
            handleNavigateToDeviceTable(widgetSection);
            setOpen(false);
          }}
        >
          {t('machineDashboard.deviceTable')}
        </KccButton>
        <KccButton size="small" type="primary" onClick={(): void => setOpen(false)}>
          {t('common.close')}
        </KccButton>
      </div>
    </StyledMachineKpiPopover>
  );

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement={placement}
      autoAdjustOverflow={false}
      arrow={false}
      styles={{
        body: { padding: 0, boxShadow: '0px 0px 30px 0px #0000000D' },
        root: { width: 450 },
      }}
      content={popoverContent}
    >
      <span ref={triggerRef}>{children}</span>
    </Popover>
  );
};
